import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { ProductType } from '@tink/core/src/types';
import { CredentialsEntity } from '../../models/credentials';
import { isRedirectMode as isRedirectModeImport } from '../tink-link';
import { base } from '../../lib-api/services/base';
import { buildRedirectHandlerUrl } from '../../routes';
import { applicationParameters } from '../application-parameters';
import { routeBuilder } from '../../routes/routeBuilder';
import { getMajorVersion } from '../utils';

// BankID redirects need to work for both only opening BankID but also passing in autostarttoken (eg. Skandiabanken)
//
// This needs to work on iOS:
// - Safari
// - Embeded in app (SFSafariViewController)
// - Embeded in app (UIWebView)
// - Embeded in app (WKWebView)
//
// This needs to work on Android:
// - Chrome
// - Firefox
// - Embedded in app (WebView)
//
// Inspired by:
// - http://www.ampedupdesigns.com/blog/show?bid=67
// - https://github.com/uliedberg/bankid-app-autolaunch-in-chrome-on-android-test

export const BANK_ID_DEEPLINK = `bankid:///`;

export const BANK_ID_UNIVERSAL_LINK = 'https://app.bankid.com/';

export type UserDevice = {
  isMobile: boolean;
  isChrome: boolean;
  isSamsungBrowser: boolean;
  isAppleDevice: boolean;
  isAndroidDevice: boolean;
  browserVersion: string;
};

const userDevice: UserDevice = {
  isMobile: userAgent.isMobile,
  isChrome: userAgent.isChrome,
  isSamsungBrowser: userAgent.isSamsungBrowser,
  isAppleDevice: userAgent.isAppleDevice,
  isAndroidDevice: userAgent.isAndroidDevice,
  browserVersion: userAgent.browserVersion,
};

export type BankIdRedirectorProps = {
  redirectToReferPage: boolean;
  credentials: CredentialsEntity;
  productType: ProductType;
  clientName?: string;
  device?: UserDevice;
  window?: Window;
  isRedirectMode?: boolean;
  redirectUrl?: string;
  enableCredentialsMachine?: boolean;
};

export class BankIdRedirector {
  private credentials: CredentialsEntity;

  private productType: ProductType;

  private redirectToReferPage: boolean;

  private device: UserDevice;

  private window: Window;

  private isRedirectMode: boolean;

  private redirectUrl: string | undefined;

  private enableCredentialsMachine: boolean;

  constructor({
    credentials,
    productType,
    redirectToReferPage,
    device,
    isRedirectMode = isRedirectModeImport(),
    window: windowParam,
    redirectUrl,
    enableCredentialsMachine,
  }: BankIdRedirectorProps) {
    this.credentials = credentials;
    this.productType = productType;
    this.redirectToReferPage = redirectToReferPage;
    this.device = device || userDevice;
    this.window = windowParam || window;
    this.isRedirectMode = isRedirectMode;
    this.redirectUrl = redirectUrl;
    this.enableCredentialsMachine = enableCredentialsMachine ?? false;
  }

  buildDeepLink = (redirectUrl: string | null) => {
    const { supplementalInformation } = this.credentials;

    const autoStartToken = supplementalInformation
      ? `autostarttoken=${supplementalInformation}&`
      : '';

    // The redirect parameter must be last in the parameter list. The autostarttoken and rpref parameters are optional.
    return `${BANK_ID_DEEPLINK}?${autoStartToken}redirect=${redirectUrl}`;
  };

  getAndroidIntent = () => {
    if (this.redirectUrl) {
      return this.buildDeepLink(this.redirectUrl);
    }

    let redirectUrl = applicationParameters.get('appUri') || null;

    // Don't try to redirect back if it's iframe
    if (!this.isRedirectMode && !applicationParameters.get('appUri')) {
      redirectUrl = null;
    }

    const intent = this.buildDeepLink(redirectUrl);

    // Alternative #1 (build intent URI) works APP - Y, FIREFOX - Y, CHROME - Y
    // Alternative #2 (directly use deeplink URI) works APP - Y, FIREFOX - Y, CHROME - N
    if (this.device.isChrome) {
      const search = intent.replace('bankid://', '');

      return `intent://${search}#Intent;scheme=bankid;package=com.bankid.bus;end`;
    }

    return intent;
  };

  getDeepOrUniversalLinkForIos = () => {
    const { origin, search: searchQuery, pathname } = this.window.location;
    const clientName = base.clientName || '';
    const appUri = applicationParameters.get('appUri');

    if (this.redirectUrl) {
      return this.buildDeepLink(this.redirectUrl);
    }

    if (appUri) {
      return this.buildDeepLink(appUri);
    }

    if (
      !this.enableCredentialsMachine &&
      this.isRedirectMode &&
      this.productType === ProductType.PAY
    ) {
      // Generally: In redirect mode we redirect to a new tab and let the old tab go stale
      // Exception: When we are doing payments. We have not yet implemented support for
      // handling the old and new window(https://tinkab.atlassian.net/browse/SDK-1290)
      // Instead we rely on the redirect to the last tab in the browser tab stack
      const redirectUrl = routeBuilder.buildTppToWebUrl.build({ origin, clientName });

      return this.buildDeepLink(redirectUrl);
    }

    if (this.isRedirectMode) {
      const authorizationToken = base.authorizationToken || '';
      const trackingId = applicationParameters.get('trackingId') as string;

      let redirectUrl = buildRedirectHandlerUrl({
        origin,
        pathname,
        searchQuery,
        trackingId,
        authorizationToken,
      });
      // Appending `credentialsId` manually since BankId doesn't redirect back through api.tink.com
      redirectUrl += `&credentialsId=${this.credentials.id}`;

      return this.buildDeepLink(window.encodeURIComponent(redirectUrl));
    }

    // Don't try to redirect back if it's iframe (redirectMode=false)
    return this.buildDeepLink(null);
  };

  open() {
    if (!this.device.isMobile) {
      // TODO: Should we support BankID web?
      return;
    }

    if (this.device.isAppleDevice) {
      this.#navigate(this.getDeepOrUniversalLinkForIos());
    }

    if (this.device.isAndroidDevice) {
      this.#navigate(this.getAndroidIntent());
    }
  }

  shouldPatchForChromeV112OnAndroid() {
    if (this.isRedirectMode || !this.device.isChrome) {
      return false;
    }
    const { isAndroidDevice, isChrome, browserVersion } = this.device;

    // Only for Iframe, on Android, with Chrome V112 and above.
    // Note: Relies on browser name to be "Chrome WebView" on Android Webview
    if (isAndroidDevice && isChrome && !this.isRedirectMode) {
      return getMajorVersion(browserVersion) >= 112;
    }

    return false;
  }

  shouldPatchForSamsungBrowserV22OnAndroid() {
    if (this.isRedirectMode || !this.device.isSamsungBrowser) {
      return false;
    }
    const { isAndroidDevice, isSamsungBrowser, browserVersion } = this.device;

    // Only for Iframe, on Android, with Samsung Browser V22 and above.
    if (isAndroidDevice && isSamsungBrowser && !this.isRedirectMode) {
      return getMajorVersion(browserVersion) >= 22;
    }

    return false;
  }

  #navigate(url: string) {
    if (this.isRedirectMode) {
      this.window.location.href = url;
    } else if (this.shouldPatchForChromeV112OnAndroid()) {
      this.window.location.href = url;
    } else if (this.shouldPatchForSamsungBrowserV22OnAndroid()) {
      this.window.location.href = url;
    } else if (this.window.top) {
      this.window.top.location.href = url;
    }
  }
}
